import { useState, useCallback, useEffect } from "react";
import { Box, Text, Stack, Group, Button, Switch, Select, ActionIcon, Collapse, TextInput, Combobox, useCombobox, InputBase, ScrollAreaAutosize, NumberInput } from "@mantine/core";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import {
  useViewerStore,
  useEndCoordsList,
  useAvailableLinks,
  useEndCoordsLocalPosition,
  useEndCoordsLocalRotation,
  useEndCoordsSnapEnabled,
  useEndCoordsPositionSnap,
  useEndCoordsRotationSnap,
  useEditLinksMode,
} from "../../store";
import { useWebSocket } from "../../WebSocket";

// Position snap options
const POSITION_SNAP_OPTIONS = [
  { value: "0.001", label: "0.001m (1mm)" },
  { value: "0.005", label: "0.005m (5mm)" },
  { value: "0.01", label: "0.01m (1cm)" },
  { value: "0.05", label: "0.05m (5cm)" },
  { value: "0.1", label: "0.1m (10cm)" },
];

// Rotation snap options (in radians)
const ROTATION_SNAP_OPTIONS = [
  { value: String(Math.PI / 16), label: "π/16 (11.25°)" },
  { value: String(Math.PI / 8), label: "π/8 (22.5°)" },
  { value: String(Math.PI / 4), label: "π/4 (45°)" },
  { value: String(Math.PI / 2), label: "π/2 (90°)" },
];

export function EndCoordsSection() {
  const endCoordsList = useEndCoordsList();
  const hiddenEndCoords = useViewerStore((state) => state.hiddenEndCoords);
  const toggleEndCoordsVisibility = useViewerStore((state) => state.toggleEndCoordsVisibility);
  const editLinksMode = useEditLinksMode();
  const availableLinks = useAvailableLinks();
  const endCoordsLocalPosition = useEndCoordsLocalPosition();
  const endCoordsLocalRotation = useEndCoordsLocalRotation();
  const snapEnabled = useEndCoordsSnapEnabled();
  const positionSnap = useEndCoordsPositionSnap();
  const rotationSnap = useEndCoordsRotationSnap();
  const setEndCoordsSnapEnabled = useViewerStore((state) => state.setEndCoordsSnapEnabled);
  const setEndCoordsPositionSnap = useViewerStore((state) => state.setEndCoordsPositionSnap);
  const setEndCoordsRotationSnap = useViewerStore((state) => state.setEndCoordsRotationSnap);
  const { sendAddEndCoords, sendUpdateEndCoords, sendDeleteEndCoords, sendHighlightInstance, sendShowEndCoordsGizmo } = useWebSocket();

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingName, setEditingName] = useState<string | null>(null);

  // Form state for adding/editing
  const [formName, setFormName] = useState("");
  const [formParentLink, setFormParentLink] = useState<string | null>(null);
  const [formPosition, setFormPosition] = useState<[number, number, number]>([0, 0, 0]);
  const [formRotation, setFormRotation] = useState<[number, number, number]>([0, 0, 0]);

  // Track if gizmo is active (to sync updates from gizmo to form)
  const [gizmoActive, setGizmoActive] = useState(false);

  // Combobox state for parent link selection
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      // Clear highlight when dropdown closes
      sendHighlightInstance("");
    },
  });
  const [searchValue, setSearchValue] = useState("");

  // Sync form position/rotation from gizmo updates
  useEffect(() => {
    if (gizmoActive) {
      setFormPosition([...endCoordsLocalPosition]);
      setFormRotation([...endCoordsLocalRotation]);
    }
  }, [gizmoActive, endCoordsLocalPosition, endCoordsLocalRotation]);

  // Show/hide gizmo when parent link changes
  useEffect(() => {
    if (editLinksMode) {
      return;
    }
    if (formParentLink && (showAddForm || editingName !== null)) {
      sendShowEndCoordsGizmo(true, formParentLink, formPosition, formRotation);
      setGizmoActive(true);
    } else {
      sendShowEndCoordsGizmo(false, "", [0, 0, 0], [0, 0, 0]);
      setGizmoActive(false);
    }
  }, [formParentLink, showAddForm, editingName, editLinksMode]);

  // Edit Links mode owns the shared gizmo. Force-hide custom end coords gizmo while active.
  useEffect(() => {
    if (!editLinksMode) {
      return;
    }
    if (gizmoActive) {
      sendShowEndCoordsGizmo(false, "", [0, 0, 0], [0, 0, 0]);
      setGizmoActive(false);
    }
  }, [editLinksMode, gizmoActive, sendShowEndCoordsGizmo]);

  // Update gizmo when form values change manually
  const updateGizmoFromForm = useCallback((newPosition: [number, number, number], newRotation: [number, number, number]) => {
    if (editLinksMode) {
      return;
    }
    if (formParentLink && gizmoActive) {
      sendShowEndCoordsGizmo(true, formParentLink, newPosition, newRotation);
    }
  }, [editLinksMode, formParentLink, gizmoActive, sendShowEndCoordsGizmo]);

  const resetForm = () => {
    setFormName("");
    setFormParentLink(null);
    setFormPosition([0, 0, 0]);
    setFormRotation([0, 0, 0]);
    setShowAddForm(false);
    setEditingName(null);
    setSearchValue("");
    sendShowEndCoordsGizmo(false, "", [0, 0, 0], [0, 0, 0]);
    setGizmoActive(false);
  };

  const handleAdd = () => {
    if (!formName || !formParentLink) return;
    sendAddEndCoords(formName, formParentLink, formPosition, formRotation);
    resetForm();
  };

  const handleUpdate = () => {
    if (!editingName || !formParentLink) return;
    sendUpdateEndCoords(editingName, formParentLink, formPosition, formRotation);
    resetForm();
  };

  const handleEdit = (name: string) => {
    const ec = endCoordsList.find((e) => e.name === name);
    if (!ec) return;
    setEditingName(name);
    setFormName(ec.name);
    setFormParentLink(ec.parent_link_name);
    setFormPosition([...ec.position]);
    setFormRotation([...ec.rotation]);
    setShowAddForm(false);
    setSearchValue(ec.parent_link_name);
  };

  const handleDelete = (name: string) => {
    sendDeleteEndCoords(name);
  };

  // Parse instance_id from link name (format: "instance_id:link_name")
  const parseInstanceId = (linkName: string): string => {
    if (linkName.includes(":")) {
      return linkName.split(":")[0];
    }
    return "";
  };

  // Get display name for link (show link_name, hide instance_id prefix)
  const getLinkDisplayName = (linkName: string): string => {
    if (linkName.includes(":")) {
      const parts = linkName.split(":");
      return `${parts[1]} (${parts[0].slice(0, 8)}...)`;
    }
    return linkName;
  };

  // Filter links based on search
  const filteredLinks = availableLinks.filter((link) =>
    link.toLowerCase().includes(searchValue.toLowerCase())
  );

  const isEditing = editingName !== null;
  const formVisible = showAddForm || isEditing;

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="xs" fw={500}>Custom End Coords</Text>
        {!formVisible && (
          <Button
            size="xs"
            variant="light"
            onClick={() => setShowAddForm(true)}
            disabled={availableLinks.length === 0}
          >
            + Add
          </Button>
        )}
      </Group>

      {/* Gizmo Snap Settings */}
      <Box
        p="xs"
        style={{
          border: "1px solid var(--mantine-color-gray-3)",
          borderRadius: 4,
          backgroundColor: "var(--mantine-color-gray-0)",
        }}
      >
        <Group justify="space-between" mb="xs">
          <Text size="xs" fw={500}>Gizmo Snapping</Text>
          <Switch
            size="xs"
            checked={snapEnabled}
            onChange={(e) => setEndCoordsSnapEnabled(e.currentTarget.checked)}
          />
        </Group>
        {snapEnabled && (
          <Group grow gap="xs">
            <Select
              label="Position"
              size="xs"
              data={POSITION_SNAP_OPTIONS}
              value={String(positionSnap)}
              onChange={(val) => val && setEndCoordsPositionSnap(parseFloat(val))}
              allowDeselect={false}
            />
            <Select
              label="Rotation"
              size="xs"
              data={ROTATION_SNAP_OPTIONS}
              value={String(rotationSnap)}
              onChange={(val) => val && setEndCoordsRotationSnap(parseFloat(val))}
              allowDeselect={false}
            />
          </Group>
        )}
      </Box>

      {availableLinks.length === 0 && (
        <Text size="xs" c="dimmed">Add modules to create custom end coords</Text>
      )}

      {/* Existing end coords list */}
      {endCoordsList.map((ec) => (
        <Box
          key={ec.name}
          p="xs"
          style={{
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: 4,
            backgroundColor: editingName === ec.name ? "var(--mantine-color-blue-0)" : undefined,
          }}
        >
          <Group justify="space-between" align="flex-start">
            <Box>
              <Text size="xs" fw={500}>{ec.name}</Text>
              <Text size="xs" c="dimmed">{ec.parent_link_name}</Text>
            </Box>
            <Group gap={4}>
              <ActionIcon
                size="xs"
                variant="subtle"
                color={hiddenEndCoords[ec.name] ? "gray" : "blue"}
                onClick={() => toggleEndCoordsVisibility(ec.name)}
                title={hiddenEndCoords[ec.name] ? "Show" : "Hide"}
              >
                {hiddenEndCoords[ec.name] ? <IconEyeOff size={14} /> : <IconEye size={14} />}
              </ActionIcon>
              <Button
                size="xs"
                variant="subtle"
                px={6}
                onClick={() => handleEdit(ec.name)}
                disabled={isEditing && editingName !== ec.name}
              >
                Edit
              </Button>
              <Button
                size="xs"
                variant="subtle"
                color="red"
                px={6}
                onClick={() => handleDelete(ec.name)}
              >
                Del
              </Button>
            </Group>
          </Group>
        </Box>
      ))}

      {/* Add/Edit form */}
      <Collapse in={formVisible}>
        <Box
          p="sm"
          style={{
            border: "1px solid var(--mantine-color-blue-3)",
            borderRadius: 4,
            backgroundColor: "var(--mantine-color-blue-0)",
          }}
        >
          <Stack gap="xs">
            <Text size="xs" fw={500}>{isEditing ? `Edit: ${editingName}` : "Add End Coords"}</Text>

            {!isEditing && (
              <TextInput
                label="Name"
                placeholder="e.g., gripper_tip"
                size="xs"
                value={formName}
                onChange={(e) => setFormName(e.currentTarget.value)}
              />
            )}

            <Combobox
              store={combobox}
              onOptionSubmit={(val) => {
                setFormParentLink(val);
                setSearchValue(getLinkDisplayName(val));
                sendHighlightInstance(""); // Clear highlight on select
                combobox.closeDropdown();
              }}
            >
              <Combobox.Target>
                <InputBase
                  label="Parent Link"
                  placeholder="Select or search link"
                  size="xs"
                  value={searchValue}
                  onChange={(event) => {
                    setSearchValue(event.currentTarget.value);
                    combobox.openDropdown();
                    combobox.updateSelectedOptionIndex();
                  }}
                  onClick={() => combobox.openDropdown()}
                  onFocus={() => combobox.openDropdown()}
                  onBlur={() => {
                    combobox.closeDropdown();
                    // Restore display name if a link was selected
                    if (formParentLink) {
                      setSearchValue(getLinkDisplayName(formParentLink));
                    }
                  }}
                  rightSection={<Combobox.Chevron />}
                  rightSectionPointerEvents="none"
                />
              </Combobox.Target>

              <Combobox.Dropdown>
                <Combobox.Options>
                  <ScrollAreaAutosize mah={200}>
                    {filteredLinks.length > 0 ? (
                      filteredLinks.map((link) => (
                        <Combobox.Option
                          key={link}
                          value={link}
                          onMouseEnter={() => sendHighlightInstance(parseInstanceId(link))}
                          onMouseLeave={() => sendHighlightInstance("")}
                        >
                          {getLinkDisplayName(link)}
                        </Combobox.Option>
                      ))
                    ) : (
                      <Combobox.Empty>No links found</Combobox.Empty>
                    )}
                  </ScrollAreaAutosize>
                </Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>

            <Box>
              <Text size="xs" c="dimmed" mb={4}>Position Offset (m)</Text>
              <Group grow>
                <NumberInput
                  label="X"
                  size="xs"
                  value={formPosition[0]}
                  onChange={(val) => {
                    const newPos: [number, number, number] = [typeof val === "number" ? val : 0, formPosition[1], formPosition[2]];
                    setFormPosition(newPos);
                    updateGizmoFromForm(newPos, formRotation);
                  }}
                  step={0.01}
                  decimalScale={3}
                  hideControls
                />
                <NumberInput
                  label="Y"
                  size="xs"
                  value={formPosition[1]}
                  onChange={(val) => {
                    const newPos: [number, number, number] = [formPosition[0], typeof val === "number" ? val : 0, formPosition[2]];
                    setFormPosition(newPos);
                    updateGizmoFromForm(newPos, formRotation);
                  }}
                  step={0.01}
                  decimalScale={3}
                  hideControls
                />
                <NumberInput
                  label="Z"
                  size="xs"
                  value={formPosition[2]}
                  onChange={(val) => {
                    const newPos: [number, number, number] = [formPosition[0], formPosition[1], typeof val === "number" ? val : 0];
                    setFormPosition(newPos);
                    updateGizmoFromForm(newPos, formRotation);
                  }}
                  step={0.01}
                  decimalScale={3}
                  hideControls
                />
              </Group>
            </Box>

            <Box>
              <Text size="xs" c="dimmed" mb={4}>Rotation RPY (rad)</Text>
              <Group grow>
                <NumberInput
                  label="Roll"
                  size="xs"
                  value={formRotation[0]}
                  onChange={(val) => {
                    const newRot: [number, number, number] = [typeof val === "number" ? val : 0, formRotation[1], formRotation[2]];
                    setFormRotation(newRot);
                    updateGizmoFromForm(formPosition, newRot);
                  }}
                  step={0.1}
                  decimalScale={3}
                  hideControls
                />
                <NumberInput
                  label="Pitch"
                  size="xs"
                  value={formRotation[1]}
                  onChange={(val) => {
                    const newRot: [number, number, number] = [formRotation[0], typeof val === "number" ? val : 0, formRotation[2]];
                    setFormRotation(newRot);
                    updateGizmoFromForm(formPosition, newRot);
                  }}
                  step={0.1}
                  decimalScale={3}
                  hideControls
                />
                <NumberInput
                  label="Yaw"
                  size="xs"
                  value={formRotation[2]}
                  onChange={(val) => {
                    const newRot: [number, number, number] = [formRotation[0], formRotation[1], typeof val === "number" ? val : 0];
                    setFormRotation(newRot);
                    updateGizmoFromForm(formPosition, newRot);
                  }}
                  step={0.1}
                  decimalScale={3}
                  hideControls
                />
              </Group>
            </Box>

            <Group justify="flex-end" gap="xs">
              <Button size="xs" variant="default" onClick={resetForm}>
                Cancel
              </Button>
              <Button
                size="xs"
                color="blue"
                onClick={isEditing ? handleUpdate : handleAdd}
                disabled={isEditing ? !formParentLink : !formName || !formParentLink}
              >
                {isEditing ? "Update" : "Add"}
              </Button>
            </Group>
          </Stack>
        </Box>
      </Collapse>
    </Stack>
  );
}
